import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl, consumeSessionSupersededFlash, SESSION_SUPERSEDED_MESSAGE } from '../services/api';
import { getImageUrl } from '../utils/imageUrl';
import LandingNavbar from './common/LandingNavbar';
import './LandingPage.css';

const LandingPage = () => {
  const { t, i18n } = useTranslation('landing');
  const navigate = useNavigate();
  const location = useLocation();
  const isRTL = i18n.language === 'ar';
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [currentHeroImage, setCurrentHeroImage] = useState(0);
  const [showKnowledgeTip, setShowKnowledgeTip] = useState(false);
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);

  // Load logo from Banner Management (website banner with title containing "logo")
  useEffect(() => {
    const base = getApiBaseUrl();
    const url = base ? `${base}/api/banners?type=website&active=true` : '/api/banners?type=website&active=true';
    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        if (!data || !data.success || !Array.isArray(data.banners)) return;
        const logoBanner = data.banners.find((b) => (b.title || '').toLowerCase().includes('logo'));
        if (logoBanner && logoBanner.image_url) setLogoUrl(logoBanner.image_url);
      })
      .catch(() => {});
  }, []);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      const isMobile = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobileDevice(isMobile);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Hero images configuration
  const heroSlides = [
    {
      photoId: '1522202176988-66273c2fd55f',
      alt: 'Students learning together',
      type: 'vocabulary' // First slide: Vocabulary focus
    },
    {
      photoId: '1521737604893-d14cc237f11d',
      alt: 'Online education',
      type: 'courses' // Second slide: Course Library focus
    },
    {
      photoId: '1516321318423-f06f85e504b3',
      alt: 'People in video call conversation',
      type: 'speaking' // Third slide: Speaking & Free Talk focus
    }
  ];

  // Function to generate Unsplash URL with proper parameters
  const getUnsplashUrl = (photoId, width, quality = 80) => {
    return `https://images.unsplash.com/photo-${photoId}?w=${width}&fit=crop&auto=format&q=${quality}`;
  };


  const [heroAutoplayId, setHeroAutoplayId] = useState(0);

  const goToHeroSlide = (index) => {
    const total = heroSlides.length;
    setCurrentHeroImage(((index % total) + total) % total);
    setHeroAutoplayId((id) => id + 1);
  };

  const goToPrevHeroSlide = () => {
    setCurrentHeroImage((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
    setHeroAutoplayId((id) => id + 1);
  };

  const goToNextHeroSlide = () => {
    setCurrentHeroImage((prev) => (prev + 1) % heroSlides.length);
    setHeroAutoplayId((id) => id + 1);
  };

  // Rotate hero slides every 8 seconds (increased from 6 for better visibility)
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeroImage((prev) => (prev + 1) % heroSlides.length);
    }, 8000); // 8 seconds - gives users more time to read each slide

    return () => clearInterval(interval);
  }, [heroSlides.length, heroAutoplayId]);

  // Show knowledge tip after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowKnowledgeTip(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const flash = consumeSessionSupersededFlash();
    if (params.get('session') === 'superseded' || flash) {
      setError(flash || SESSION_SUPERSEDED_MESSAGE);
      if (params.get('session') === 'superseded') {
        params.delete('session');
        const nextSearch = params.toString();
        navigate(
          { pathname: '/', search: nextSearch ? `?${nextSearch}` : '', hash: location.hash || '#login' },
          { replace: true }
        );
      }
    }
  }, [location.search, location.hash, navigate]);

  useEffect(() => {
    const id = location.state?.scrollTo || (location.hash || '').replace('#', '');
    if (!id) return undefined;

    const scrollToTarget = () => {
      const element = document.getElementById(id);
      if (!element) return false;
      element.scrollIntoView({ behavior: 'smooth' });
      return true;
    };

    if (scrollToTarget()) return undefined;

    const timers = [50, 200, 500].map((delay) => setTimeout(scrollToTarget, delay));
    return () => timers.forEach(clearTimeout);
  }, [location.hash, location.state]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setLoginData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Universal login endpoint - works for all account types (students, teachers, admins). getApiBaseUrl() avoids mixed content on HTTPS.
      const API_BASE_URL = getApiBaseUrl();
      let response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: (loginData.email || '').trim().toLowerCase(),
          password: loginData.password
        })
      });

      // Check if response is ok before parsing JSON
      if (!response.ok && response.status !== 401) {
        // Handle rate limiting (429) errors specifically
        if (response.status === 429) {
          const retryAfter = response.headers.get('Retry-After') || response.headers.get('retry-after');
          const retryTime = retryAfter ? ` Please try again after ${retryAfter} seconds.` : ' Please try again in 15 minutes.';
          setError('Too many login attempts. ' + retryTime);
          setLoading(false);
          return;
        }
        
        // For other non-401 errors, try to parse error message
        try {
          const errorData = await response.json();
          setError(errorData.message || errorData.error || 'Failed to connect to server. Please try again.');
          setLoading(false);
          return;
        } catch (parseError) {
          setError('Failed to connect to server. Please check your internet connection.');
          setLoading(false);
          return;
        }
      }

      let data = await response.json();

      // If universal login fails, try admin login as fallback (for admin accounts not in database)
      if (!response.ok || !data.success) {
        response = await fetch(`${API_BASE_URL}/api/admin/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: loginData.email,
            password: loginData.password
          })
        });
        
        if (!response.ok && response.status !== 401) {
          try {
            const errorData = await response.json();
            setError(errorData.message || 'Invalid email or password');
            setLoading(false);
            return;
          } catch (parseError) {
            setError('Failed to connect to server. Please check your internet connection.');
            setLoading(false);
            return;
          }
        }
        
        data = await response.json();
      }

      if (response.ok && data.success) {
        // Store access token
        if (data.token) {
          localStorage.setItem('token', data.token);
        }
        
        // Store refresh token if provided
        if (data.refreshToken) {
          localStorage.setItem('refreshToken', data.refreshToken);
        }
        
        // Store user info
        if (data.user) {
          // If role is not in user object, fetch it from user_roles table
          let userRole = data.user?.role?.toLowerCase();
          
          // Always fetch role from API to ensure we have the correct role from user_roles table
          if (data.user.id) {
            try {
              const roleResponse = await fetch(`${API_BASE_URL}/api/users/${data.user.id}/role`, {
                method: 'GET',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${data.token || localStorage.getItem('token')}`
                }
              });
              
              if (roleResponse.ok) {
                const roleData = await roleResponse.json();
                userRole = roleData.role?.toLowerCase();
                // Update user object with role
                data.user.role = roleData.role;
                console.log('✅ Fetched user role from API:', userRole);
              } else {
                console.warn('⚠️ Failed to fetch role, using role from user object:', userRole);
              }
            } catch (err) {
              console.error('❌ Error fetching user role:', err);
              console.warn('⚠️ Using role from user object:', userRole);
            }
          }
          
          console.log('🔍 Final userRole before routing:', userRole);
          localStorage.setItem('user', JSON.stringify(data.user));
          
          // Automatically redirect to the correct dashboard based on user role
          if (userRole === 'admin') {
            console.log('➡️ Routing to admin dashboard (admin)');
            navigate('/admin-dashboard');
          } else if (userRole === 'agent') {
            console.log('➡️ Routing to admin dashboard (agent)');
            navigate('/admin-dashboard');  // Same dashboard, sidebar filtered
          } else if (userRole === 'moderator') {
            console.log('➡️ Routing to admin dashboard (moderator)');
            navigate('/admin-dashboard');  // Same dashboard, sidebar filtered (can approve payments)
          } else if (userRole === 'teacher') {
            console.log('➡️ Routing to teacher dashboard');
            navigate('/teacher-dashboard');
          } else if (userRole === 'parent') {
            console.log('➡️ Routing to parent dashboard');
            navigate('/parent-dashboard');
          } else if (userRole === 'school_admin') {
            console.log('➡️ Routing to business (school) dashboard');
            navigate('/business/dashboard');
          } else if (userRole === 'school') {
            console.log('➡️ Routing to school partner dashboard');
            navigate('/school-dashboard');
          } else {
            // Student or default: user dashboard
            console.log('➡️ Routing to user dashboard (student/default)');
            navigate('/user-dashboard');
          }
        } else {
          // No user data, redirect to user dashboard as fallback
          navigate('/user-dashboard');
        }
      } else {
        setError(data.message || 'Invalid email or password');
      }
    } catch (err) {
      console.error('Login error:', err);
      // Handle network errors more gracefully
      if (err.name === 'TypeError' && err.message === 'Failed to fetch') {
        setError('Network error: Please check your internet connection and try again.');
      } else {
        setError('Failed to connect to server. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Features array - using translations
  const features = [
    {
      icon: 'school',
      title: t('features.educationalLevels.title'),
      description: t('features.educationalLevels.description'),
      color: '#1a73e8'
    },
    {
      icon: 'autorenew',
      title: t('features.aiPowered.title'),
      description: t('features.aiPowered.description'),
      color: '#1a73e8'
    },
    {
      icon: 'analytics',
      title: t('features.trackProgress.title'),
      description: t('features.trackProgress.description'),
      color: '#34a853'
    },
    {
      icon: 'menu_book',
      title: t('features.learningPaths.title'),
      description: t('features.learningPaths.description'),
      color: '#1a73e8'
    },
    {
      icon: 'record_voice_over',
      title: t('features.learnWithTeachers.title'),
      description: t('features.learnWithTeachers.description'),
      color: '#ea4335'
    },
    {
      icon: 'assignment',
      title: t('features.examPreparation.title'),
      description: t('features.examPreparation.description'),
      color: '#fbbc04'
    }
  ];

  // Testimonials array - using translations
  const testimonials = [
    {
      name: t('testimonials.testimonial1.name'),
      role: t('testimonials.testimonial1.role'),
      image: '👤',
      quote: t('testimonials.testimonial1.quote'),
      rating: 5
    },
    {
      name: t('testimonials.testimonial2.name'),
      role: t('testimonials.testimonial2.role'),
      image: '👤',
      quote: t('testimonials.testimonial2.quote'),
      rating: 5
    },
    {
      name: t('testimonials.testimonial3.name'),
      role: t('testimonials.testimonial3.role'),
      image: '👤',
      quote: t('testimonials.testimonial3.quote'),
      rating: 5
    },
    {
      name: t('testimonials.testimonial4.name'),
      role: t('testimonials.testimonial4.role'),
      image: '👤',
      quote: t('testimonials.testimonial4.quote'),
      rating: 5
    },
    {
      name: t('testimonials.testimonial5.name'),
      role: t('testimonials.testimonial5.role'),
      image: '👤',
      quote: t('testimonials.testimonial5.quote'),
      rating: 5
    },
    {
      name: t('testimonials.testimonial6.name'),
      role: t('testimonials.testimonial6.role'),
      image: '👤',
      quote: t('testimonials.testimonial6.quote'),
      rating: 5
    }
  ];

  // Stats array - using translations
  const stats = [
    { number: '10,000+', label: t('stats.activeLearners') },
    { number: '95%', label: t('stats.improvementRate') },
    { number: '4.8/5', label: t('stats.averageRating') },
    { number: '50+', label: t('stats.schoolsTrust') }
  ];

  return (
    <div className="landing-page">
      <LandingNavbar />

      {/* Hero Section - Full Screen Visual with Rotating Images & Content */}
      <section className="hero-section-fullscreen">
        {/* Rotating Background Images - Using <picture> for proper responsive images */}
        {/* On mobile: Show only first image, on desktop: Show all 3 rotating */}
        <div className="hero-background-container">
          {heroSlides.map((slide, index) => {
            // On mobile, only show first image (index 0), but text still rotates
            if (isMobileDevice && index !== 0) return null;
            
            return (
            <picture
              key={`hero-bg-${index}`}
              className={`hero-background-image ${index === currentHeroImage ? 'active' : ''} ${isMobileDevice ? 'mobile-single-image' : ''}`}
            >
              {/* Mobile: 640px width, lower quality for faster loading */}
              {/* Include 1280w for retina displays (DPR=2) to prevent upscaling */}
              <source
                media="(max-width: 640px)"
                srcSet={`
                  ${getUnsplashUrl(slide.photoId, 640, 60)} 640w,
                  ${getUnsplashUrl(slide.photoId, 1280, 60)} 1280w
                `}
                sizes="100vw"
              />
              {/* Tablet: 1024px width */}
              {/* Include 2048w for retina displays */}
              <source
                media="(min-width: 641px) and (max-width: 1024px)"
                srcSet={`
                  ${getUnsplashUrl(slide.photoId, 1024, 70)} 1024w,
                  ${getUnsplashUrl(slide.photoId, 2048, 70)} 2048w
                `}
                sizes="100vw"
              />
              {/* Desktop: 1920px width, higher quality */}
              {/* Removed width/height attributes - let CSS handle sizing to prevent forced 1920px download */}
              <img
                src={getUnsplashUrl(slide.photoId, 1920, 80)}
                alt={slide.alt}
                className="hero-background-img"
                loading={index === 0 ? 'eager' : 'lazy'}
                decoding="async"
                fetchPriority={index === 0 ? 'high' : 'low'}
                sizes="100vw"
              />
            </picture>
            );
          })}
          {/* Dark gradient overlay for text readability */}
          <div className="hero-overlay-gradient"></div>
        </div>

        {/* Rotating Hero Content Overlays */}
        {heroSlides.map((slide, index) => (
          <div
            key={`hero-content-${index}`}
            className={`hero-content-overlay ${index === currentHeroImage ? 'active' : ''}`}
          >
            {slide.type === 'vocabulary' ? (
              // Vocabulary Hero Content
              <div className="hero-content-wrapper">
                <div className="trust-badge">
                  <span className="material-icons">menu_book</span>
                  <span>{t('hero.vocabulary.badge')}</span>
                </div>
                
                <h1 className="hero-title-fullscreen">
                  {t('hero.vocabulary.title')}<span className="highlight">{t('hero.vocabulary.titleHighlight')}</span>
                </h1>
                
                <p className="hero-subtitle-fullscreen">
                  {t('hero.vocabulary.subtitle')}
                </p>

                <div className="hero-stats-fullscreen">
                  <div className="stat-item-fullscreen">
                    <div className="stat-number-fullscreen">10,000+</div>
                    <div className="stat-label-fullscreen">{t('hero.vocabulary.statWords')}</div>
                  </div>
                  <div className="stat-item-fullscreen">
                    <div className="stat-number-fullscreen">15+</div>
                    <div className="stat-label-fullscreen">{t('hero.vocabulary.statLevels')}</div>
                  </div>
                  <div className="stat-item-fullscreen">
                    <div className="stat-number-fullscreen">95%</div>
                    <div className="stat-label-fullscreen">{t('hero.vocabulary.statRetention')}</div>
                  </div>
                  <div className="stat-item-fullscreen">
                    <div className="stat-number-fullscreen">24/7</div>
                    <div className="stat-label-fullscreen">{t('hero.vocabulary.statStudents')}</div>
                  </div>
                </div>

                <div className="hero-cta-fullscreen">
                  <button className="btn-primary large" onClick={() => navigate('/signup')}>
                    <span className="material-icons">explore</span>
                    {t('hero.vocabulary.browseVocabulary')}
                  </button>
                  <button className="btn-secondary large" onClick={() => scrollToSection('features')}>
                    <span className="material-icons">play_arrow</span>
                    {t('hero.vocabulary.startLearning')}
                  </button>
                </div>

                <p className="hero-note-fullscreen">
                  <span className="material-icons">check_circle</span>
                  {t('hero.vocabulary.note')}
                </p>
              </div>
            ) : slide.type === 'courses' ? (
              // Course Library Hero Content
              <div className="hero-content-wrapper">
                <div className="trust-badge">
                  <span className="material-icons">library_books</span>
                  <span>{t('hero.courses.badge')}</span>
                </div>
                
                <h1 className="hero-title-fullscreen">
                  {t('hero.courses.title')} <span className="highlight">{t('hero.courses.titleHighlight')}</span>
                </h1>
                
                <p className="hero-subtitle-fullscreen">
                  {t('hero.courses.subtitle')}
                </p>

                <div className="hero-stats-fullscreen">
                  <div className="stat-item-fullscreen">
                    <div className="stat-number-fullscreen">500+</div>
                    <div className="stat-label-fullscreen">{t('hero.courses.statCourses')}</div>
                  </div>
                  <div className="stat-item-fullscreen">
                    <div className="stat-number-fullscreen">50+</div>
                    <div className="stat-label-fullscreen">{t('hero.courses.statSubjects')}</div>
                  </div>
                  <div className="stat-item-fullscreen">
                    <div className="stat-number-fullscreen">Live</div>
                    <div className="stat-label-fullscreen">{t('hero.courses.statWorkshops')}</div>
                  </div>
                  <div className="stat-item-fullscreen">
                    <div className="stat-number-fullscreen">24/7</div>
                    <div className="stat-label-fullscreen">{t('hero.courses.statAccess')}</div>
                  </div>
                </div>

                <div className="hero-cta-fullscreen">
                  <button className="btn-primary large" onClick={() => navigate('/signup')}>
                    <span className="material-icons">explore</span>
                    {t('hero.courses.browseCourses')}
                  </button>
                  <button className="btn-secondary large" onClick={() => scrollToSection('features')}>
                    <span className="material-icons">live_tv</span>
                    {t('hero.courses.joinLiveSession')}
                  </button>
                </div>

                <p className="hero-note-fullscreen">
                  <span className="material-icons">check_circle</span>
                  {t('hero.courses.note')}
                </p>
              </div>
            ) : (
              // Speaking & Free Talk Hero Content
              <div className="hero-content-wrapper">
                <div className="trust-badge">
                  <span className="material-icons">record_voice_over</span>
                  <span>{t('hero.speaking.badge')}</span>
                </div>
                
                <h1 className="hero-title-fullscreen">
                  {t('hero.speaking.title')} <span className="highlight">{t('hero.speaking.titleHighlight')}</span>
                </h1>
                
                <p className="hero-subtitle-fullscreen">
                  {t('hero.speaking.subtitle')}
                </p>

                <div className="hero-stats-fullscreen">
                  <div className="stat-item-fullscreen">
                    <div className="stat-number-fullscreen">Live</div>
                    <div className="stat-label-fullscreen">{t('hero.speaking.statConversations')}</div>
                  </div>
                  <div className="stat-item-fullscreen">
                    <div className="stat-number-fullscreen">Global</div>
                    <div className="stat-label-fullscreen">{t('hero.speaking.statCommunity')}</div>
                  </div>
                  <div className="stat-item-fullscreen">
                    <div className="stat-number-fullscreen">24/7</div>
                    <div className="stat-label-fullscreen">{t('hero.speaking.statAvailable')}</div>
                  </div>
                  <div className="stat-item-fullscreen">
                    <div className="stat-number-fullscreen">Free</div>
                    <div className="stat-label-fullscreen">{t('hero.speaking.statTalkSessions')}</div>
                  </div>
                </div>

                <div className="hero-cta-fullscreen">
                  <button className="btn-primary large" onClick={() => navigate('/signup')}>
                    <span className="material-icons">mic</span>
                    {t('hero.speaking.joinFreeTalk')}
                  </button>
                  <button className="btn-secondary large" onClick={() => scrollToSection('features')}>
                    <span className="material-icons">info</span>
                    {t('hero.speaking.learnMore')}
                  </button>
                </div>

                <p className="hero-note-fullscreen">
                  <span className="material-icons">check_circle</span>
                  {t('hero.speaking.note')}
                </p>
              </div>
            )}
          </div>
        ))}

        <button
          type="button"
          className="hero-arrow hero-arrow-prev"
          onClick={goToPrevHeroSlide}
          aria-label={t('hero.prevSlide')}
        >
          <span className="material-icons">{isRTL ? 'chevron_right' : 'chevron_left'}</span>
        </button>
        <button
          type="button"
          className="hero-arrow hero-arrow-next"
          onClick={goToNextHeroSlide}
          aria-label={t('hero.nextSlide')}
        >
          <span className="material-icons">{isRTL ? 'chevron_left' : 'chevron_right'}</span>
        </button>

        {/* Image Rotation Indicators */}
        <div className="hero-indicators">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              className={`hero-indicator ${index === currentHeroImage ? 'active' : ''}`}
              onClick={() => goToHeroSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Trust Bar */}
      <section className="trust-bar">
        <div className="trust-container">
          <p className="trust-label">{t('trustedBy.title')}</p>
          <div className="trust-logos">
            <div className="trust-logo">
              <span className="material-icons">school</span>
              <span>500+ {t('trustedBy.schools')}</span>
            </div>
            <div className="trust-logo">
              <span className="material-icons">account_balance</span>
              <span>50+ {t('trustedBy.universities')}</span>
            </div>
            <div className="trust-logo">
              <span className="material-icons">business</span>
              <span>{t('trustedBy.institutions')}</span>
            </div>
            <div className="trust-logo">
              <span className="material-icons">groups</span>
              <span>10,000+ {t('trustedBy.students')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">{t('features.title')}</h2>
            <p className="section-subtitle">{t('features.subtitle')}</p>
          </div>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon" style={{ backgroundColor: `${feature.color}15`, color: feature.color }}>
                  <span className="material-icons">{feature.icon}</span>
                </div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="how-it-works-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">{t('howItWorks.title')}</h2>
            <p className="section-subtitle">{t('howItWorks.subtitle')}</p>
          </div>
          <div className="steps-container">
            {(() => {
              const steps = [
                { step: 1, title: t('howItWorks.step1.title'), desc: t('howItWorks.step1.description'), icon: 'school' },
                { step: 2, title: t('howItWorks.step2.title'), desc: t('howItWorks.step2.description'), icon: 'psychology' },
                { step: 3, title: t('howItWorks.step3.title'), desc: t('howItWorks.step3.description'), icon: 'menu_book' },
                { step: 4, title: t('howItWorks.step4.title'), desc: t('howItWorks.step4.description'), icon: 'trending_up' },
                { step: 5, title: t('howItWorks.step5.title'), desc: t('howItWorks.step5.description'), icon: 'emoji_events' }
              ];
              return (isRTL ? [...steps].reverse() : steps);
            })().map((step, index, array) => (
              <div key={index} className="step-item">
                <div className="step-number">{step.step}</div>
                <div className="step-icon">
                  <span className="material-icons">{step.icon}</span>
                </div>
                <h3 className="step-title">{step.title}</h3>
                <p className="step-desc">{step.desc}</p>
                {index < array.length - 1 && (
                  <div className="step-arrow">
                    <span className="material-icons">{isRTL ? 'arrow_back' : 'arrow_forward'}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="testimonials-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">{t('testimonials.title')}</h2>
            <p className="section-subtitle">{t('testimonials.subtitle')}</p>
          </div>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card">
                <div className="testimonial-rating">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="material-icons">star</span>
                  ))}
                </div>
                <p className="testimonial-quote">"{testimonial.quote}"</p>
                <div className="testimonial-author">
                  <div className="author-avatar">{testimonial.image}</div>
                  <div className="author-info">
                    <div className="author-name">{testimonial.name}</div>
                    <div className="author-role">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">{t('whyChoose.title')}</h2>
            <p className="section-subtitle">{t('whyChoose.subtitle')}</p>
          </div>
          <div className="why-choose-grid">
            <div className="why-choose-card">
              <div className="why-choose-icon">
                <span className="material-icons">verified</span>
              </div>
              <h3 className="why-choose-title">{t('whyChoose.cefrAligned.title')}</h3>
              <p className="why-choose-desc">{t('whyChoose.cefrAligned.description')}</p>
            </div>
            <div className="why-choose-card">
              <div className="why-choose-icon">
                <span className="material-icons">school</span>
              </div>
              <h3 className="why-choose-title">{t('whyChoose.educationalLevels.title')}</h3>
              <p className="why-choose-desc">{t('whyChoose.educationalLevels.description')}</p>
            </div>
            <div className="why-choose-card">
              <div className="why-choose-icon">
                <span className="material-icons">psychology</span>
              </div>
              <h3 className="why-choose-title">{t('whyChoose.aiPowered.title')}</h3>
              <p className="why-choose-desc">{t('whyChoose.aiPowered.description')}</p>
            </div>
            <div className="why-choose-card">
              <div className="why-choose-icon">
                <span className="material-icons">trending_up</span>
              </div>
              <h3 className="why-choose-title">{t('whyChoose.progressTracking.title')}</h3>
              <p className="why-choose-desc">{t('whyChoose.progressTracking.description')}</p>
            </div>
            <div className="why-choose-card">
              <div className="why-choose-icon">
                <span className="material-icons">live_tv</span>
              </div>
              <h3 className="why-choose-title">{t('whyChoose.liveRecorded.title')}</h3>
              <p className="why-choose-desc">{t('whyChoose.liveRecorded.description')}</p>
            </div>
            <div className="why-choose-card">
              <div className="why-choose-icon">
                <span className="material-icons">public</span>
              </div>
              <h3 className="why-choose-title">{t('whyChoose.allSubjects.title')}</h3>
              <p className="why-choose-desc">{t('whyChoose.allSubjects.description')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="pricing-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">{t('pricing.title')}</h2>
            <p className="section-subtitle">{t('pricing.subtitle')}</p>
          </div>
          <div className="pricing-grid">
            <div className="pricing-card">
              <div className="pricing-header">
                <h3 className="pricing-name">{t('pricing.free.title')}</h3>
                <div className="pricing-price">
                  <span className="price-amount">$0</span>
                  <span className="price-period">/month</span>
                </div>
              </div>
              <ul className="pricing-features">
                <li><span className="material-icons">check</span> {t('pricing.free.features.basicVocabulary')}</li>
                <li><span className="material-icons">check</span> {t('pricing.free.features.limitedArticles')}</li>
                <li><span className="material-icons">check</span> {t('pricing.free.features.communitySupport')}</li>
              </ul>
              <button className="btn-outline" onClick={() => navigate('/signup')}>{t('pricing.free.cta')}</button>
            </div>

            <div className="pricing-card featured">
              <div className="pricing-badge">{t('pricing.premium.badge')}</div>
              <div className="pricing-header">
                <h3 className="pricing-name">{t('pricing.premium.title')}</h3>
                <div className="pricing-price">
                  <span className="price-amount">$9.99</span>
                  <span className="price-period">/month</span>
                </div>
              </div>
              <ul className="pricing-features">
                <li><span className="material-icons">check</span> {t('pricing.premium.features.allFeatures')}</li>
                <li><span className="material-icons">check</span> {t('pricing.premium.features.unlimitedContent')}</li>
                <li><span className="material-icons">check</span> {t('pricing.premium.features.liveClasses')}</li>
                <li><span className="material-icons">check</span> {t('pricing.premium.features.advancedAnalytics')}</li>
                <li><span className="material-icons">check</span> {t('pricing.premium.features.prioritySupport')}</li>
              </ul>
              <button className="btn-primary" onClick={() => navigate('/signup')}>{t('pricing.premium.cta')}</button>
              <p className="pricing-note">{t('pricing.premium.trial')}</p>
            </div>

            <div className="pricing-card">
              <div className="pricing-header">
                <h3 className="pricing-name">{t('pricing.student.title')}</h3>
                <div className="pricing-price">
                  <span className="price-amount">$4.99</span>
                  <span className="price-period">/month</span>
                </div>
                <p className="pricing-discount">{t('pricing.student.discount')}</p>
              </div>
              <ul className="pricing-features">
                <li><span className="material-icons">check</span> {t('pricing.student.features.allPremium')}</li>
                <li><span className="material-icons">check</span> {t('pricing.student.features.studentVerification')}</li>
                <li><span className="material-icons">check</span> {t('pricing.student.features.educationalResources')}</li>
              </ul>
              <button className="btn-outline" onClick={() => navigate('/signup')}>{t('pricing.student.cta')}</button>
            </div>
          </div>
          <div className="pricing-guarantee">
            <span className="material-icons">verified_user</span>
            <span>{t('pricing.guarantee')}</span>
          </div>

          {/* Yearly plans — Languages & Supervision (DA) */}
          <div className="pricing-yearly">
            <div className="pricing-yearly-header">
              <h3 className="pricing-yearly-title">{t('pricing.yearlyPlans.title')}</h3>
              <p className="pricing-yearly-subtitle">{t('pricing.yearlyPlans.subtitle')}</p>
            </div>

            <div className="pricing-yearly-grid">
              {[
                {
                  key: 'primary-2l',
                  title: t('pricing.yearlyPlans.cards.primary.title'),
                  languagesLabel: t('pricing.yearlyPlans.cards.primary.languages'),
                  price: t('pricing.yearlyPlans.cards.primary.price'),
                  supervision: t('pricing.yearlyPlans.cards.primary.supervision'),
                  features: [
                    t('pricing.yearlyPlans.cards.primary.features.ai'),
                    t('pricing.yearlyPlans.cards.primary.features.curriculum'),
                    t('pricing.yearlyPlans.cards.primary.features.supervision')
                  ]
                },
                {
                  key: 'middle-2l',
                  title: t('pricing.yearlyPlans.cards.middle.title'),
                  languagesLabel: t('pricing.yearlyPlans.cards.middle.languages'),
                  price: t('pricing.yearlyPlans.cards.middle.price'),
                  supervision: t('pricing.yearlyPlans.cards.middle.supervision'),
                  features: [
                    t('pricing.yearlyPlans.cards.middle.features.advanced'),
                    t('pricing.yearlyPlans.cards.middle.features.srs'),
                    t('pricing.yearlyPlans.cards.middle.features.supervision')
                  ]
                },
                {
                  key: 'high-2l',
                  title: t('pricing.yearlyPlans.cards.high2.title'),
                  languagesLabel: t('pricing.yearlyPlans.cards.high2.languages'),
                  price: t('pricing.yearlyPlans.cards.high2.price'),
                  supervision: t('pricing.yearlyPlans.cards.high2.supervision'),
                  features: [
                    t('pricing.yearlyPlans.cards.high2.features.exams'),
                    t('pricing.yearlyPlans.cards.high2.features.mastery'),
                    t('pricing.yearlyPlans.cards.high2.features.supervision')
                  ]
                },
                {
                  key: 'high-3l',
                  title: t('pricing.yearlyPlans.cards.high3.title'),
                  languagesLabel: t('pricing.yearlyPlans.cards.high3.languages'),
                  price: t('pricing.yearlyPlans.cards.high3.price'),
                  supervision: t('pricing.yearlyPlans.cards.high3.supervision'),
                  features: [
                    t('pricing.yearlyPlans.cards.high3.features.multilingual'),
                    t('pricing.yearlyPlans.cards.high3.features.mastery'),
                    t('pricing.yearlyPlans.cards.high3.features.supervisionIncluded')
                  ],
                  featured: true
                }
              ].map((p) => (
                <div key={p.key} className={`pricing-card pricing-yearly-card ${p.featured ? 'featured' : ''}`}>
                  {p.featured ? <div className="pricing-badge">{t('pricing.premium.badge')}</div> : null}
                  <div className="pricing-header">
                    <h3 className="pricing-name">{p.title}</h3>
                    <div className="pricing-yearly-price">
                      <div className="pricing-yearly-amount">{p.price}</div>
                      <div className="pricing-yearly-supervision">{p.supervision}</div>
                    </div>
                  </div>
                  <div className="pricing-yearly-languages">{p.languagesLabel}</div>
                  <ul className="pricing-features">
                    {p.features.map((f) => (
                      <li key={f}>
                        <span className="material-icons">check</span>
                        {f}
                      </li>
                    ))}
                  </ul>
                  <button className="btn-primary" onClick={() => navigate('/signup')}>{t('pricing.yearlyPlans.cta')}</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">{t('faq.title')}</h2>
          </div>
          <div className="faq-list">
            {[
              {
                q: t('faq.q1.question'),
                a: t('faq.q1.answer')
              },
              {
                q: t('faq.q2.question'),
                a: t('faq.q2.answer')
              },
              {
                q: t('faq.q3.question'),
                a: t('faq.q3.answer')
              },
              {
                q: t('faq.q4.question'),
                a: t('faq.q4.answer')
              },
              {
                q: t('faq.q5.question'),
                a: t('faq.q5.answer')
              }
            ].map((faq, index) => (
              <div key={index} className="faq-item">
                <div className="faq-question">
                  <span className="material-icons">help_outline</span>
                  <span>{faq.q}</span>
                </div>
                <div className="faq-answer">{faq.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section cta-container">
        <h2 className="cta-title">{t('cta.title')}</h2>
        <p className="cta-subtitle">{t('cta.subtitle')}</p>
        <button className="btn-primary large" onClick={() => navigate('/signup')}>
          {t('cta.button')}
        </button>
        <p className="cta-note">{t('cta.note')}</p>
      </section>

      {/* Login Modal */}
      <section id="login" className="login-section">
        <div className="section-container">
          <div className="login-container">
            <div className="login-box">
              <h2 className="login-title">{t('login.title')}</h2>
              <p className="login-subtitle">{t('login.subtitle')}</p>
              <form onSubmit={handleLogin} className="login-form">
                {error && <div className="error-message">
                  <span className="material-icons">error</span>
                  {error}
                </div>}
                <div className="input-group">
                  <span className="material-icons">email</span>
                  <input
                    type="email"
                    name="email"
                    placeholder={t('login.email')}
                    value={loginData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="input-group">
                  <span className="material-icons">lock</span>
                  <input
                    type="password"
                    name="password"
                    placeholder={t('login.password')}
                    value={loginData.password}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <button type="submit" className="btn-primary full-width" disabled={loading}>
                  {loading ? t('messages.loading', { ns: 'common' }) : t('login.button')}
                </button>
                <div className="forgot-password-link">
                  <a href="/forgot-password" onClick={(e) => { e.preventDefault(); navigate('/forgot-password'); }}>
                    {t('login.forgotPassword')}
                  </a>
                </div>
                <p className="login-footer">
                  {t('login.noAccount')}{' '}
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/signup'); }}>
                    {t('login.signUp')}
                  </a>
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-container">
          <div className="footer-section">
            <div className="footer-logo">
              {logoUrl ? (
                <img src={getImageUrl(logoUrl) || logoUrl} alt="Syllera" className="landing-logo-img" />
              ) : (
                <span className="material-icons">school</span>
              )}
              <span>Syllera</span>
            </div>
            <p className="footer-description">
              {t('footer.tagline')}
            </p>
          </div>
          <div className="footer-section">
            <h4>{t('footer.product')}</h4>
            <ul>
              <li><a href="/#features" onClick={(e) => { e.preventDefault(); scrollToSection('features'); }}>{t('nav.features')}</a></li>
              <li><a href="/#pricing" onClick={(e) => { e.preventDefault(); scrollToSection('pricing'); }}>{t('nav.pricing')}</a></li>
              <li><a href="/#how-it-works" onClick={(e) => { e.preventDefault(); scrollToSection('how-it-works'); }}>{t('nav.howItWorks')}</a></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>{t('footer.company')}</h4>
            <ul>
              <li><a href="/about">{t('footer.about')}</a></li>
              <li><a href="/contact">{t('footer.contact')}</a></li>
              <li><a href="/blog">{t('footer.blog')}</a></li>
              <li><a href="/partners">{t('footer.becomeAPartner')}</a></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>{t('footer.support')}</h4>
            <ul>
              <li><a href="/#faq" onClick={(e) => { e.preventDefault(); scrollToSection('faq'); }}>{t('footer.faq')}</a></li>
              <li><a href="/help-center">{t('footer.helpCenter')}</a></li>
              <li><a href="/contact-support">{t('footer.contactSupport')}</a></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>{t('footer.copyright')}</p>
          <div className="footer-links">
            <a href="/privacy-policy">{t('footer.privacyPolicy')}</a>
            <a href="/terms-of-service">{t('footer.termsOfService')}</a>
            <a href="/cookie-policy">{t('footer.cookiePolicy')}</a>
            <a href="/refund-policy">{t('footer.refundPolicy')}</a>
          </div>
        </div>
      </footer>

      {/* Back to Top Button */}
      {isScrolled && (
        <button 
          className="back-to-top" 
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="Back to top"
        >
          <span className="material-icons">arrow_upward</span>
        </button>
      )}
    </div>
  );
};

export default LandingPage;
